/* ============================================================================
   01 · Hero reveal
   ----------------------------------------------------------------------------
   Pattern: masked line reveal against a settling image, then a parallax exit.

   What's being decided here:
     • How long the hero holds before the page feels usable. The full entry
       runs ~1.6s; anything past ~2s and the first-time visitor has already
       started scrolling through the animation.
     • Whether the image settles from zoom BEFORE or WITH the copy. Currently
       it overlaps by design — the copy rides the tail of the image settle so
       the two read as one gesture rather than two events.
     • The exit. Copy leaves faster than the image (2:1), which is what sells
       depth. Drop that ratio to 1:1 and the hero flattens.

   Try: set Speed to 0.2x and watch the overlap. Then set --mo-ease-entrance to
   power2.out in motion.css and watch it go generic.
   ========================================================================= */

import { initMotion, refreshOnSettle, isReduced, MOTION } from '../../src/motion.js';
import { mountLabBar } from '../../src/lab.js';

const { gsap, ScrollTrigger } = initMotion();
mountLabBar({ title: '01 · Hero reveal' });
refreshOnSettle();

const lines = gsap.utils.toArray('[data-hero-line]');
let entry;
let exit;

function buildEntry() {
  entry?.kill();

  // Reset to the pre-animation state so Replay is honest — a rebuilt timeline
  // that starts from wherever the last one ended looks fine and proves nothing.
  gsap.set(lines, { yPercent: 120 });
  gsap.set('[data-hero-image]', { scale: MOTION.scale.in * 1.06, transformOrigin: '50% 60%' });
  gsap.set(
    ['[data-hero-eyebrow]', '[data-hero-sub]', '[data-hero-actions]', '[data-hero-cue]'],
    { opacity: 0, y: MOTION.rise.sm }
  );

  entry = gsap.timeline({ defaults: { ease: MOTION.ease.entrance } });

  entry
    // The image starts first and runs longest — it's the bed everything else
    // lands on, so it should already be in motion when the copy arrives.
    .to('[data-hero-image]', {
      scale: 1,
      duration: MOTION.dur.cinematic,
      ease: MOTION.ease.emphasis,
    })
    .to(
      '[data-hero-eyebrow]',
      { opacity: 1, y: 0, duration: MOTION.dur.slow },
      MOTION.dur.quick
    )
    // Masked lines: the stagger is what makes this read as typesetting rather
    // than a block fade. Tighten below ~60ms and the lines fuse into one.
    .to(
      lines,
      {
        yPercent: 0,
        duration: MOTION.dur.deliberate,
        stagger: MOTION.stagger.loose,
      },
      MOTION.dur.quick * 1.5
    )
    // Sub and actions land under the last headline line, not after it. The
    // overlap is the difference between "choreographed" and "queued".
    .to('[data-hero-sub]', { opacity: 1, y: 0, duration: MOTION.dur.slow }, '-=0.45')
    .to('[data-hero-actions]', { opacity: 1, y: 0, duration: MOTION.dur.slow }, '-=0.35')
    .to('[data-hero-cue]', { opacity: 1, y: 0, duration: MOTION.dur.base }, '-=0.2');

  return entry;
}

function buildExit() {
  exit?.kill();
  if (isReduced()) return; // no scroll-linked motion under reduced

  // Scrubbed, so it's driven by scroll position rather than time — the lab's
  // Speed control won't affect this. Scroll slowly to judge it.
  exit = gsap.timeline({
    scrollTrigger: {
      trigger: '.hero',
      start: 'top top',
      end: 'bottom top',
      scrub: true,
    },
  });

  exit
    .to(
      '.hero__content',
      { yPercent: -40, opacity: 0, ease: MOTION.ease.linear },
      0
    )
    .to(
      '[data-hero-image]',
      { yPercent: 20 * MOTION.parallax.mid * 6, ease: MOTION.ease.linear },
      0
    )
    .to('[data-hero-cue]', { opacity: 0, ease: MOTION.ease.linear }, 0);
}

function build() {
  ScrollTrigger?.getAll().forEach((t) => t.kill());
  buildEntry();
  buildExit();
}

build();
window.addEventListener('mo:replay', build);
